import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Typography, Form, Input, Select, Button, message, Result, Card, Space, Alert,
} from 'antd';
import {
  SendOutlined, ArrowLeftOutlined, ToolOutlined, MessageOutlined,
} from '@ant-design/icons';
import { feedbackAPI, toolsAPI } from '../services/api';
import { useLanguage } from '../store/LanguageContext';
import { availabilityCNLabels } from '../constants/availabilityCN';
import type { AvailabilityCN } from '../types';

const { Title, Paragraph } = Typography;
const { TextArea } = Input;

const FeedbackPage: React.FC = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const toolId = searchParams.get('toolId') ? Number(searchParams.get('toolId')) : undefined;
  const isToolMode = !!toolId;
  const intentFromUrl = searchParams.get('intent') || undefined;
  const typeFromUrl = searchParams.get('type') || undefined;

  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [toolName, setToolName] = useState<string>('');
  const [toolSlug, setToolSlug] = useState<string>('');

  useEffect(() => {
    if (toolId) {
      toolsAPI.getToolById(toolId).then(res => {
        const tool = res.data?.data || res.data;
        if (tool) {
          setToolName(tool.name);
          setToolSlug(tool.slug || '');
        }
      }).catch(() => { /* ignore */ });
    }
    const init: Record<string, string> = {};
    if (typeFromUrl) init.type = typeFromUrl;
    if (intentFromUrl === 'seeking_funding' || intentFromUrl === 'investing') init.intent = intentFromUrl;
    if (Object.keys(init).length) form.setFieldsValue(init);
  }, [toolId, typeFromUrl, intentFromUrl, form]);

  const handleSubmit = async (values: any) => {
    setSubmitting(true);
    try {
      const payload: any = {
        type: isToolMode ? 'TOOL_CORRECTION' : values.type,
        message: values.message.trim(),
        pageUrl: window.location.href,
      };
      if (values.contact) payload.contact = values.contact.trim();
      if (toolId) payload.toolId = toolId;
      if (values.availabilityCnSuggestion) payload.availabilityCnSuggestion = values.availabilityCnSuggestion;
      if (values.evidenceUrl) payload.evidenceUrl = values.evidenceUrl.trim();
      if (values.intent === 'seeking_funding' || values.intent === 'investing') {
        payload.intent = values.intent;
        if (!isToolMode) payload.type = 'BUSINESS';
      }

      await feedbackAPI.submit(payload);
      setSubmitted(true);
      message.success(t('feedback.submitSuccess'));
    } catch (err: any) {
      const errorMsg = err.response?.data?.errors?.[0]?.msg || err.response?.data?.error || t('feedback.submitFailed');
      message.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div style={{ maxWidth: 600, margin: '48px auto', padding: '0 16px' }}>
        <Result
          status="success"
          title={t('feedback.submitSuccess')}
          subTitle={t('feedback.submitSuccessDesc')}
          extra={[
            isToolMode && toolSlug ? (
              <Button key="tool" type="primary" onClick={() => navigate(`/tools/${toolSlug}`)}>
                {t('feedback.backTool')}
              </Button>
            ) : (
              <Button key="home" type="primary" onClick={() => navigate('/')}>
                {t('feedback.backHome')}
              </Button>
            ),
            <Button key="another" onClick={() => { setSubmitted(false); form.resetFields(); }}>
              {t('feedback.submitAnother')}
            </Button>,
          ].filter(Boolean)}
        />
      </div>
    );
  }

  const feedbackTypeOptions = [
    { value: 'GENERAL', label: t('feedback.typeGeneral') },
    { value: 'BUG', label: t('feedback.typeBug') },
    { value: 'BUSINESS', label: t('feedback.typeBusiness') },
    { value: 'DATA_ERROR', label: t('feedback.typeDataError') },
  ];

  const availabilityOptions = (Object.keys(availabilityCNLabels) as AvailabilityCN[]).map(key => ({
    value: key,
    label: availabilityCNLabels[key],
  }));

  return (
    <div style={{ maxWidth: 640, margin: '32px auto', padding: '0 16px' }}>
      <Button
        type="text"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate(-1)}
        style={{ marginBottom: 16 }}
      >
        {t('common.back')}
      </Button>

      <Card>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          {isToolMode ? (
            <>
              <ToolOutlined style={{ fontSize: 36, color: '#1677ff', marginBottom: 12 }} />
              <Title level={3} style={{ margin: 0 }}>
                {t('feedback.correctionTitle')}
              </Title>
              {toolName && (
                <Paragraph type="secondary" style={{ marginTop: 8 }}>
                  {t('feedback.forTool', { name: toolName })}
                </Paragraph>
              )}
            </>
          ) : (
            <>
              <MessageOutlined style={{ fontSize: 36, color: '#1677ff', marginBottom: 12 }} />
              <Title level={3} style={{ margin: 0 }}>
                {t('feedback.title')}
              </Title>
              <Paragraph type="secondary" style={{ marginTop: 8 }}>
                {t('feedback.subtitle')}
              </Paragraph>
            </>
          )}
        </div>

        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
          message={t('feedback.alertMessage')}
        />

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ type: 'GENERAL' }}
        >
          {!isToolMode && (
            <Form.Item
              name="type"
              label={t('feedback.type')}
              rules={[{ required: true, message: t('feedback.typeRequired') }]}
            >
              <Select options={feedbackTypeOptions} />
            </Form.Item>
          )}

          {!isToolMode && (
            <Form.Item
              name="intent"
              label={language === 'zh-CN' ? '融资/投资意向（可选）' : 'Funding intent (optional)'}
            >
              <Select
                allowClear
                options={[
                  { value: 'seeking_funding', label: language === 'zh-CN' ? '寻求融资' : 'Seeking funding' },
                  { value: 'investing', label: language === 'zh-CN' ? '投资人找标的' : 'Investor' },
                ]}
              />
            </Form.Item>
          )}

          <Form.Item
            name="message"
            label={t('feedback.message')}
            rules={[
              { required: true, message: t('feedback.messageRequired') },
              { min: 5, message: t('feedback.messageMin') },
              { max: 5000, message: t('feedback.messageMax') },
            ]}
          >
            <TextArea
              rows={5}
              placeholder={t('feedback.messagePlaceholder')}
              showCount
              maxLength={5000}
            />
          </Form.Item>

          {isToolMode && (
            <>
              <Form.Item
                name="availabilityCnSuggestion"
                label={t('feedback.availabilitySuggestion')}
              >
                <Select
                  allowClear
                  placeholder={t('feedback.selectOptional')}
                  options={availabilityOptions}
                />
              </Form.Item>

              <Form.Item
                name="evidenceUrl"
                label={t('feedback.evidenceUrl')}
                rules={[{ type: 'url', message: t('feedback.invalidUrl') }]}
              >
                <Input placeholder={t('feedback.evidenceUrlPlaceholder')} />
              </Form.Item>
            </>
          )}

          <Form.Item
            name="contact"
            label={t('feedback.contact')}
          >
            <Input placeholder={t('feedback.contactPlaceholder')} maxLength={200} />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={submitting}
              icon={<SendOutlined />}
              size="large"
              block
            >
              {t('feedback.submit')}
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default FeedbackPage;
